import React, { useEffect, useState, useCallback } from 'react';
import cn from 'classnames';
import { observer, calculatorState as store } from 'store';
import Modal from 'components/UI/Modal';
import CloseButton from 'components/UI/CloseButton';
import Title from 'components/UI/Title';
import Button from 'components/UI/Button';
import CountBox from '../CountBox/CountBox';
import MobileBlock from '../MobileBlock';
import { getTotal, getApplyStatus } from '../../utils';
import { useTrans } from 'utils/localisation';
import * as styles from './MobileSectionModal.module.scss';

function MobileSectionModal({
  isOpen,
  calculatorID,
  blockType,
  type,
  sectionName,
  totalBoxType,
  counterType,
  title,
  subtitleMobile,
  items,
  options,
  initialStep,
  defaultValue,
  onClose: closeModalCallback,
  onShowList,
}) {
  const { getSectionInfo, updateSectionInfo, updateCurrentList } = store;
  const [state, setState] = useState({ firstOpen: true, values: {}, total: 0, disableApply: true });
  const info = getSectionInfo(calculatorID, sectionName);
  const isActiveCountBox = state.total ? true : totalBoxType !== 'string';
  const t = useTrans(['calculator']);

  useEffect(() => {
    const values = { ...info };
    setState(prev => ({
      ...prev,
      firstOpen: false,
      values: { ...prev.values, ...values },
      total: getTotal(values, totalBoxType),
      disableApply: getApplyStatus(info, values),
    }));
  }, [isOpen]);

  const changeCountHandler = useCallback(
    (key, value) =>
      setState(prev => {
        const values = { ...prev.values, [key]: value };
        const total = getTotal(values, totalBoxType);
        const disableApply = getApplyStatus(info, values);

        return { ...prev, values, total, disableApply };
      }),
    [info],
  );

  const changeRadioHandler = useCallback(
    (name, value) => {
      setState(prev => {
        const [prevKey] = Object.entries(prev.values).find(([, value]) => value) || [];
        const values = prevKey
          ? { ...prev.values, [prevKey]: false, [name]: value ?? true }
          : { ...prev.values, [name]: value ?? true };
        const total = getTotal(values, totalBoxType);
        const disableApply = getApplyStatus(info, values);

        return { ...prev, values, total, disableApply };
      });
    },
    [info],
  );

  const clickApplyHandler = useCallback(() => {
    updateSectionInfo(calculatorID, sectionName, state.values);

    Object.entries(state.values).forEach(([name, value]) =>
      updateCurrentList(calculatorID, sectionName, name, value),
    );

    onShowList();
  }, [calculatorID, sectionName, state.values, updateSectionInfo]);

  const getCounterValue = key => {
    return state.values[key];
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModalCallback={closeModalCallback}
      containerMod="xs"
      contentMod="indent"
      closeButton={false}
    >
      {onClose => (
        <>
          <CloseButton onClose={onClose} />
          <div className={styles.content}>
            <Title className={styles.title} tag="h3">
              <span className={styles.titleText}>{title}</span>
              {counterType !== 'range' && (
                <CountBox total={state.total} active={isActiveCountBox} />
              )}
            </Title>
            <div className={cn(counterType !== 'range' && styles.blockWrapper)}>
              <MobileBlock
                type={type}
                blockType={blockType}
                counterType={counterType}
                calculatorID={calculatorID}
                sectionName={sectionName}
                onChangeCount={
                  counterType === 'radio' || counterType === 'range'
                    ? changeRadioHandler
                    : changeCountHandler
                }
                initialStep={initialStep}
                options={options}
                defaultValue={defaultValue}
                items={items}
                getCounterValue={getCounterValue}
                values={state.values}
                subtitleMobile={subtitleMobile}
              />
            </div>
            <div className={styles.apply}>
              <Button
                fullWidthMobile
                disabled={
                  counterType === 'range' ? !state.total : !state.total && state.disableApply
                }
                onClick={() => {
                  clickApplyHandler();
                  onClose();
                }}
              >
                {t('apply')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

export default observer(MobileSectionModal);
